import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useChangePasswordStore } from '@lib/store';
import { PaletteUtils } from '@shared/utils';
import { Success } from '@widgets';
import { Button, TextInput } from '@shared/widgets';

interface IChangePasswordForm {
  oldPassword: string;
  password: string;
  password2: string;
}
const DEFAULT_VALUES: IChangePasswordForm = {
  oldPassword: '',
  password: '',
  password2: '',
};

interface PasswordProps {
  className?: string;
}

export const Password = (props: PasswordProps) => {
  const changePasswordStore = useChangePasswordStore();

  const { control, formState, handleSubmit } = useForm<IChangePasswordForm>({ defaultValues: DEFAULT_VALUES });
  const { t } = useTranslation('profile');

  const onSubmit = async (data: IChangePasswordForm) =>
    await changePasswordStore.change({ newPassword: data.password, oldPassword: data.oldPassword });

  if (changePasswordStore.success) {
    return (
      <div className={clsx('border border-[#e1e1e1] p-10', props.className)}>
        <div className={'h-[400px] relative'}>
          <Success text={t('changePassword.success.text')} />
        </div>
      </div>
    );
  }
  return (
    <div className={clsx('bg-white border border-[#e1e1e1] p-10 max-[840px]:p-5')}>
      <div className='my-0 max-w-[1050px]'>
        <div className='inline-block xpy-0 px-[62px] align-top w-[63%] max-[840px]:w-full max-[840px]:px-0'>
          <div className='shadow-[4px_4px_8px_0_rgba(0,0,0,0.03)] border border-[#c9c9c9] font-sans p-[26px_29px_34px_29px] max-w-[330px] mb-4'>
            <div
              className={clsx(
                'text-[16px] font-semibold -tracking-[0.1px] mb-2',
                PaletteUtils.isBordierPalette ? 'text-[#dc291c]' : 'text-[#be8b45]',
              )}
            >
              {t('changePassword.passwordHints.title')}
            </div>
            <div className='text-[#4c4c52] text-base font-normal leading-[1.3] -tracking-[0.1px] mb-[8px]'>
              {t('changePassword.passwordHints.minLength')}
              <br />
              {t('changePassword.passwordHints.number')}
              <br />
              {t('changePassword.passwordHints.capital')}
              <br />
              {t('changePassword.passwordHints.symbol')}
            </div>
          </div>
        </div>
        <form
          className='min-[840px]:inline-block min-[840px]:align-top min-[840px]:w-[37%] min-[840px]:px-[62px] min-[840px]:pt-4'
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <Controller
            name='oldPassword'
            control={control}
            rules={{ required: t('errors.form.requiredField', { ns: 'common', field: t('changePassword.placeholders.oldPassword') }) }}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                formStyle
                caption={`${t('changePassword.placeholders.oldPassword')}:`}
                type='password'
                required
                error={error?.message || changePasswordStore.error}
              />
            )}
          />
          <Controller
            name='password'
            control={control}
            rules={{
              required: t('errors.form.requiredField', { ns: 'common', field: t('changePassword.placeholders.newPassword') }),
              minLength: { value: 8, message: t('errors.form.password.minLength', { ns: 'common' }) },
              maxLength: { value: 30, message: t('errors.form.password.maxLength', { ns: 'common' }) },
              pattern: {
                value: /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^a-zA-Z0-9]).+$/,
                message: t('errors.form.password.pattern', { ns: 'common' }),
              },
              validate: (password, { password2 }) => {
                if (password !== password2) return t('changePassword.errors.form.passwordDoNotMatch');
                return true;
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                formStyle
                caption={`${t('changePassword.placeholders.newPassword')}:`}
                type='password'
                required
                error={error?.message}
              />
            )}
          />
          <Controller
            name='password2'
            control={control}
            rules={{
              required: t('errors.form.requiredField', { ns: 'common', field: t('changePassword.placeholders.retypeNewPassword') }),
              minLength: { value: 8, message: t('errors.form.password.minLength', { ns: 'common' }) },
              maxLength: { value: 30, message: t('errors.form.password.maxLength', { ns: 'common' }) },
              validate: (password2, { password }) => {
                if (password2 !== password) return t('changePassword.errors.form.passwordDoNotMatch');
                return true;
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                formStyle
                caption={`${t('changePassword.placeholders.retypeNewPassword')}:`}
                type='password'
                required
                error={error?.message}
              />
            )}
          />
          <Button
            className='leading-1 tracking-normal ml-auto min-w-[109px]'
            view='secondary'
            caption={t('buttons.update', { ns: 'common' })}
            disabled={formState.isSubmitting}
            type='submit'
          />
        </form>
      </div>
    </div>
  );
};
